import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, 
  Button, 
  Progress, 
  Alert, 
  Card, 
  Typography, 
  Space,
  notification,
  List,
  Row,
  Col
} from 'antd';
import { 
  InboxOutlined, 
  CloudUploadOutlined, 
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { videoApi } from '../services/api';
import socketService from '../services/socket';
import { getString } from '../utils/translations';

const { Dragger } = Upload;
const { Title, Text } = Typography;

// Maximum number of videos allowed per upload session
const MAX_UPLOAD_COUNT = 20;

// Track B: clients stay MP4-only until internal MOV verification completes.
// Set VITE_ALLOW_MOV_UPLOADS=true for internal/ops builds only.
const ALLOW_MOV_UPLOADS =
  String(import.meta.env.VITE_ALLOW_MOV_UPLOADS || '').toLowerCase() === 'true';
// Keep MOV available for controlled testing without advertising it to clients.
const SHOW_MOV_SUPPORT_MESSAGE = false;
const ALLOWED_VIDEO_MIME_TYPES = ALLOW_MOV_UPLOADS
  ? ['video/mp4', 'video/quicktime', 'video/mov']
  : ['video/mp4'];
const ALLOWED_VIDEO_EXTENSIONS = ALLOW_MOV_UPLOADS ? ['.mp4', '.mov'] : ['.mp4'];

const isAllowedVideoFile = (file) => {
  const fileName = (file.name || '').toLowerCase();
  const hasValidExtension = ALLOWED_VIDEO_EXTENSIONS.some((ext) => fileName.endsWith(ext));
  const mimeType = file.type || '';
  const hasValidMime = !mimeType || ALLOWED_VIDEO_MIME_TYPES.includes(mimeType);
  return hasValidExtension && hasValidMime;
};

const VideoUpload = ({ socketConnected }) => {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, processing, completed, error
  const [statusMessage, setStatusMessage] = useState('');
  const [recentUploads, setRecentUploads] = useState([]);
  // Multi-file queue state
  const [queue, setQueue] = useState([]); // [{file, jobId, progress, status, controller}]

  // Handle file selection
  const handleFileSelect = useCallback(async (file) => {
    if (!isAllowedVideoFile(file)) {
      notification.error({
        message: getString('pipeline.upload.invalid_file_type', 'Invalid File Type'),
        description: SHOW_MOV_SUPPORT_MESSAGE
          ? getString('pipeline.upload.only_mp4_mov', 'Only MP4 and MOV video files are supported.')
          : getString('pipeline.upload.only_mp4', 'Only MP4 video files are supported.'),
        duration: 7,
      });
      return false;
    }

    // File size check removed - allow unlimited client-side uploads
    // Server-side/GCS storage limits still apply

    setCurrentFile(file);
    setUploadProgress(0);
    setUploadStatus('uploading');
    setStatusMessage(getString('pipeline.upload.getting_url', 'Getting upload URL...'));
    setUploading(true);

    try {
      // Get presigned upload URL
      const presignData = await videoApi.getPresignedUrl(file.name);
      setJobId(presignData.job_id);
      setStatusMessage(getString('pipeline.upload.uploading_storage', 'Uploading to storage...'));

      // Subscribe to real-time updates if socket is connected
      if (socketConnected) {
        socketService.subscribeToJob(presignData.job_id, handleJobProgress);
      }

      // Upload file directly to storage
      await videoApi.uploadFile(
        presignData.upload_url,
        file,
        (progress) => {
          setUploadProgress(progress);
          setStatusMessage(`Uploading... ${progress}%`);
        }
      );

      setUploadProgress(100);
      setStatusMessage(getString('pipeline.upload.upload_complete_starting', 'Upload complete. Starting processing...'));

      // Notify API that upload is complete
      await videoApi.completeUpload(presignData.job_id);
      
      setUploadStatus('processing');
      setStatusMessage(getString('pipeline.upload.processing_started', 'Video processing started. You can track progress in the Job Dashboard.'));
      
      // Add to recent uploads
      const uploadInfo = {
        jobId: presignData.job_id,
        filename: file.name,
        timestamp: new Date().toISOString(),
        status: 'processing'
      };
      setRecentUploads(prev => [uploadInfo, ...prev.slice(0, 4)]);

      notification.success({
        message: getString('pipeline.upload.upload_success', 'Upload Successful'),
        description: `${file.name} ${getString('pipeline.upload.has_been_uploaded', 'has been uploaded and processing has started.')}`,
        duration: 5,
      });

    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      setStatusMessage(getString('pipeline.upload.upload_failed_try', 'Upload failed. Please try again.'));
      
      notification.error({
        message: getString('pipeline.upload.upload_failed', 'Upload Failed'),
        description: error.response?.data?.error || error.message || 'An unknown error occurred',
        duration: 5,
      });
    } finally {
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  }, [socketConnected]);

  // Handle real-time job progress updates
  const handleJobProgress = useCallback((progressData) => {
    console.log('Job progress update:', progressData);
    
    if (progressData.status === 'completed') {
      setUploadStatus('completed');
      setStatusMessage(getString('pipeline.upload.processing_complete', 'Processing Complete'));
      
      notification.success({
        message: getString('pipeline.upload.processing_complete', 'Processing Complete'),
        description: `${currentFile?.name || 'Video'} ${getString('pipeline.upload.processing_complete_desc', 'has been processed successfully.')}`,
        duration: 5,
      });
    } else if (progressData.status === 'error') {
      setUploadStatus('error');
      setStatusMessage(`Processing failed: ${progressData.error_message || 'Unknown error'}`);
      
      notification.error({
        message: getString('pipeline.upload.processing_failed', 'Processing Failed'),
        description: progressData.error_message || getString('pipeline.upload.processing_failed_desc', 'Video processing encountered an error.'),
        duration: 5,
      });
    } else if (progressData.progress !== undefined) {
      const progress = Math.round(progressData.progress);
      setStatusMessage(`Processing... ${progress}% complete`);
    }
  }, [currentFile]);

  const enqueueFile = (file) => {
    if (!isAllowedVideoFile(file)) {
      notification.error({
        message: getString('pipeline.upload.invalid_file_type', 'Invalid File Type'),
        description: SHOW_MOV_SUPPORT_MESSAGE
          ? getString('pipeline.upload.only_mp4_mov', 'Only MP4 and MOV video files are supported.')
          : getString('pipeline.upload.only_mp4', 'Only MP4 video files are supported.'),
        duration: 7,
      });
      return;
    }

    setQueue((prev) => {
      // Skip if already in queue
      if (prev.find((q) => q.file.uid === file.uid)) return prev;
      
      // Check upload limit
      if (prev.length >= MAX_UPLOAD_COUNT) {
        notification.error({
          message: getString('pipeline.upload.upload_limit_reached', 'Upload Limit Reached'),
          description: getString('pipeline.upload.upload_limit_desc', `You can only upload up to ${MAX_UPLOAD_COUNT} videos at a time. Please wait for current uploads to complete or remove some files from the queue.`),
          duration: 5,
        });
        return prev;
      }
      
      return [...prev, { file, jobId: null, progress: 0, status: 'queued', controller: null }];
    });
  };

  // Handle drag and drop
  const draggerProps = {
    name: 'video',
    multiple: true,
    accept: ALLOW_MOV_UPLOADS ? 'video/mp4,video/quicktime,.mp4,.mov' : 'video/mp4,.mp4',
    beforeUpload: (file) => {
      // enqueue rather than upload immediately
      enqueueFile(file);
      return false;
    },
    showUploadList: false,
    disabled: uploading,
  };

  useEffect(() => {
    // auto-start next queued item if idle / not currently uploading via queue
    const active = queue.find((q) => q.status === 'uploading');
    const next = queue.find((q) => q.status === 'queued');
    if (!active && next) {
      startQueuedUpload(next.file);
    }
  }, [queue]);

  const startQueuedUpload = async (file) => {
    // mark uploading
    setQueue((prev) => prev.map((q) => q.file.uid === file.uid ? { ...q, status: 'uploading', progress: 0 } : q));
    const controller = new AbortController();
    setQueue((prev) => prev.map((q) => q.file.uid === file.uid ? { ...q, controller } : q));

    // reuse single-file flow with slight adjustments
    setCurrentFile(file);
    setUploadProgress(0);
    setUploadStatus('uploading');
    setStatusMessage(getString('pipeline.upload.getting_url', 'Getting upload URL...'));
    setUploading(true);

    try {
      const presignData = await videoApi.getPresignedUrl(file.name);
      setJobId(presignData.job_id);
      if (socketConnected) {
        socketService.subscribeToJob(presignData.job_id, handleJobProgress);
      }
      setStatusMessage(getString('pipeline.upload.uploading_storage', 'Uploading to storage...'));
      await videoApi.uploadFile(presignData.upload_url, file, (p) => {
        setUploadProgress(p);
        setQueue((prev) => prev.map((q) => q.file.uid === file.uid ? { ...q, progress: p } : q));
      }, controller.signal);
      setUploadProgress(100);
      setQueue((prev) => prev.map((q) => q.file.uid === file.uid ? { ...q, progress: 100 } : q));
      setStatusMessage(getString('pipeline.upload.upload_complete_starting', 'Upload complete. Starting processing...'));
      await videoApi.completeUpload(presignData.job_id);
      setUploadStatus('processing');
      setQueue((prev) => prev.map((q) => q.file.uid === file.uid ? { ...q, status: 'processing', jobId: presignData.job_id } : q));
      // recent uploads add
      const uploadInfo = { jobId: presignData.job_id, filename: file.name, timestamp: new Date().toISOString(), status: 'processing' };
      setRecentUploads(prev => [uploadInfo, ...prev.slice(0, 4)]);
      notification.success({ message: getString('pipeline.upload.upload_success', 'Upload Successful'), description: `${file.name} ${getString('pipeline.upload.uploaded_processing_started', 'uploaded; processing started.')}`, duration: 4 });
    } catch (error) {
      console.error('Queued upload error:', error);
      setUploadStatus('error');
      setQueue((prev) => prev.map((q) => q.file.uid === file.uid ? { ...q, status: 'error' } : q));
      notification.error({ message: getString('pipeline.upload.upload_failed', 'Upload Failed'), description: error.message || 'Error uploading file', duration: 5 });
    } finally {
      setUploading(false);
    }
  };

  // Reset upload state
  const resetUpload = () => {
    setCurrentFile(null);
    setUploadProgress(0);
    setUploadStatus('idle');
    setStatusMessage('');
    setJobId(null);
    setUploading(false);
    
    if (jobId && socketConnected) {
      socketService.unsubscribeFromJob(jobId);
    }
  };

  // Navigate to job details
  const viewJobDetails = () => {
    if (jobId) {
      navigate(`/jobs/${jobId}`);
    }
  };

  // Navigate to dashboard
  const viewDashboard = () => {
    navigate('/jobs');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (jobId && socketConnected) {
        socketService.unsubscribeFromJob(jobId);
      }
    };
  }, [jobId, socketConnected]);

  return (
    <div className="upload-container">
      <Title level={1} style={{ marginBottom: 0 }}>{getString('pipeline.upload.title', 'Upload Videos for Analysis')}</Title>
      <Text type="secondary" style={{ fontSize: '1.05rem', display: 'block', marginBottom: '1.5rem' }}>
        {getString('pipeline.upload.subtitle', 'Upload your videos to start the AI analysis pipeline.')}
      </Text>

      <Alert
        message={
          <Text strong>
            {SHOW_MOV_SUPPORT_MESSAGE
              ? getString('pipeline.upload.limit_format_mov', 'Upload limit: 20 videos maximum | MP4 and MOV supported')
              : getString('pipeline.upload.limit_format_mp4_only', 'Upload limit: 20 videos maximum | MP4 format only')}
          </Text>
        }
        type="info"
        showIcon
        style={{ marginBottom: '1.5rem' }}
      />

      <Card style={{ marginBottom: '2rem' }}>
        <Dragger {...draggerProps} className="upload-area" height={200}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined className="upload-icon" />
          </p>
          <p className="upload-text">
            {getString('pipeline.upload.drop_placeholder', 'Click or drag one or more video files to upload')}
          </p>
          <p className="upload-hint">
            {SHOW_MOV_SUPPORT_MESSAGE
              ? getString('pipeline.upload.supported_format_mov', 'Supported: MP4, MOV (H.264/H.265 codec recommended)')
              : getString('pipeline.upload.supported_format_mp4_only', 'Supported format: MP4 only (H.264/H.265 codec recommended)')}
          </p>
        </Dragger>

        {uploadStatus !== 'idle' && (
          <div className="progress-container" style={{ marginTop: '2rem' }}>
            <div className="progress-header">
              <Text strong>{currentFile?.name}</Text>
              <Space>
                {uploadStatus === 'uploading' && <CloudUploadOutlined style={{ color: '#1890ff' }} />}
                {uploadStatus === 'processing' && <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />}
                {uploadStatus === 'completed' && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
                {uploadStatus === 'error' && <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
              </Space>
            </div>
            
            {uploadStatus === 'uploading' && (
              <Progress 
                percent={uploadProgress} 
                status={uploadStatus === 'error' ? 'exception' : 'active'}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
            )}
            
            <Text type="secondary" style={{ marginTop: '0.5rem', display: 'block' }}>
              {statusMessage}
            </Text>

            <Space style={{ marginTop: '1rem' }}>
              {uploadStatus === 'completed' && jobId && (
                <Button type="primary" onClick={viewJobDetails}>
                  {getString('pipeline.upload.view_results', 'View Results')}
                </Button>
              )}
              {(uploadStatus === 'processing' || uploadStatus === 'completed') && (
                <Button onClick={viewDashboard}>
                  {getString('pipeline.upload.go_to_dashboard', 'Go to Dashboard')}
                </Button>
              )}
              {(uploadStatus === 'completed' || uploadStatus === 'error') && (
                <Button onClick={resetUpload}>
                  {getString('pipeline.upload.upload_another', 'Upload Another Video')}
                </Button>
              )}
            </Space>
          </div>
        )}
      </Card>


      {/* Upload queue */}
      {queue.length > 0 && (
        <Card title={getString('pipeline.upload.upload_queue', 'Upload Queue')} style={{ marginTop: '2rem' }}>
          <List
            dataSource={queue}
            renderItem={(item) => (
              <List.Item
                actions={[
                  item.status === 'uploading' && (
                    <Button size="small" danger onClick={() => item.controller?.abort?.()}>
                      {getString('pipeline.upload.cancel', 'Cancel')}
                    </Button>
                  ),
                  (item.status === 'queued' || item.status === 'error') && (
                    <Button size="small" onClick={() => startQueuedUpload(item.file)}>{getString('pipeline.upload.start', 'Start')}</Button>
                  ),
                  item.jobId && (
                    <Button size="small" type="link" onClick={() => navigate(`/jobs/${item.jobId}`)}>{getString('pipeline.jobs.view', 'View')}</Button>
                  )
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  title={item.file.name}
                  description={
                    item.status === 'uploading' ? (
                      <Progress percent={item.progress} size="small" style={{ minWidth: 120 }} />
                    ) : (
                      <span>{item.status}</span>
                    )
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      )}

      {recentUploads.length > 0 && (
        <Card title={getString('pipeline.upload.recent_uploads', 'Recent Uploads')} style={{ marginTop: '2rem' }}>
          <List
            dataSource={recentUploads}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button 
                    type="link" 
                    onClick={() => navigate(`/jobs/${item.jobId}`)}
                  >
                    {getString('pipeline.upload.view_details', 'View Details')}
                  </Button>
                ]}
              >
                <List.Item.Meta
                  title={item.filename}
                  description={`Uploaded ${new Date(item.timestamp).toLocaleString()}`}
                />
                <div className={`status-badge status-${item.status}`}>
                  {item.status}
                </div>
              </List.Item>
            )}
          />
        </Card>
      )}
    </div>
  );
};

export default VideoUpload; 